import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function InfoCard({
  id,
  title,
  desc,
  items,
  note,
  cta,
  ctaHref,
  icon,
}: {
  id?: string;
  title: string;
  desc: string;
  items: string[];
  note?: string;
  cta: string;
  ctaHref: string;
  icon: string;
}) {
  return (
    <section id={id} className="border border-stone-200 rounded-lg bg-white p-6 md:p-8">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <i className={`${icon} text-lg`} />
        </span>
        <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900">{title}</h3>
      </div>
      <p className="text-sm md:text-base text-stone-600 leading-relaxed">{desc}</p>
      <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-stone-700">
            <i className="ri-check-line text-emerald-600 w-4 h-4 flex items-center justify-center mt-0.5" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {note && (
        <p className="mt-4 text-xs font-medium text-amber-700 bg-amber-50 rounded-md px-3 py-2">{note}</p>
      )}
      <div className="mt-5">
        <a
          href={ctaHref}
          target="_blank"
          rel="noopener nofollow"
          className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800 cursor-pointer whitespace-nowrap"
        >
          {cta}
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
        </a>
      </div>
    </section>
  );
}

export default function InfoSections() {
  const { t } = useTranslation();

  const whyCards = [
    { icon: 'ri-flashlight-line', title: t('home.why1Title'), desc: t('home.why1Desc') },
    { icon: 'ri-scales-3-line', title: t('home.why2Title'), desc: t('home.why2Desc') },
    { icon: 'ri-focus-2-line', title: t('home.why3Title'), desc: t('home.why3Desc') },
    { icon: 'ri-truck-line', title: t('home.why4Title'), desc: t('home.why4Desc') },
  ];

  const compRows = [
    [
      t('home.compRow1c1'),
      t('home.compRow1c2'),
      t('home.compRow1c3'),
      t('home.compRow1c4'),
      t('home.compRow1c5'),
      t('home.compRow1c6'),
    ],
    [
      t('home.compRow2c1'),
      t('home.compRow2c2'),
      t('home.compRow2c3'),
      t('home.compRow2c4'),
      t('home.compRow2c5'),
      t('home.compRow2c6'),
    ],
    [
      t('home.compRow3c1'),
      t('home.compRow3c2'),
      t('home.compRow3c3'),
      t('home.compRow3c4'),
      t('home.compRow3c5'),
      t('home.compRow3c6'),
    ],
    [
      t('home.compRow4c1'),
      t('home.compRow4c2'),
      t('home.compRow4c3'),
      t('home.compRow4c4'),
      t('home.compRow4c5'),
      t('home.compRow4c6'),
    ],
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-20 space-y-16">
      {/* What is */}
      <section id="what" className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900">{t('home.whatTitle')}</h2>
          <p className="mt-5 text-sm md:text-base text-stone-600 leading-relaxed">{t('home.whatP1')}</p>
          <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed">{t('home.whatP2')}</p>
          <div className="mt-6">
            <a
              href="#categories"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 cursor-pointer whitespace-nowrap"
            >
              {t('home.whatCta')}
            </a>
          </div>
        </div>
        <div className="rounded-lg overflow-hidden border border-stone-200 bg-white">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20creative%20workspace%20with%20a%20laptop%20open%20to%20a%20colorful%20spreadsheet%20interface%20next%20to%20a%20ceramic%20coffee%20cup%20and%20small%20succulent%20on%20a%20warm%20oak%20desk%2C%20soft%20morning%20light%20from%20window%2C%20clean%20editorial%20photography%20style&width=800&height=500&seq=what-spreadsheet-01&orientation=landscape"
            alt="USFans Spreadsheet overview"
            title="USFans Spreadsheet overview"
            className="w-full h-52 md:h-72 object-cover object-top"
          />
        </div>
      </section>

      {/* Why */}
      <section id="why">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center">{t('home.whyTitle')}</h2>
        <p className="mt-4 text-sm md:text-base text-stone-600 text-center">{t('home.whySubtitle')}</p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {whyCards.map((c) => (
            <div key={c.title} className="rounded-lg border border-stone-200 bg-white p-5">
              <span className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <i className={`${c.icon} text-lg`} />
              </span>
              <h3 className="mt-4 font-heading font-semibold text-stone-900 text-base">{c.title}</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Review */}
      <InfoCard
        id="review"
        icon="ri-star-line"
        title={t('home.reviewTitle')}
        desc={t('home.reviewP')}
        items={[t('home.review1'), t('home.review2'), t('home.review3'), t('home.review4'), t('home.review5'), t('home.review6')]}
        cta={t('home.reviewCta')}
        ctaHref="https://kaspreadsheet.com"
      />

      {/* Shipping */}
      <InfoCard
        id="shipping"
        icon="ri-truck-line"
        title={t('home.shipTitle')}
        desc={t('home.shipP')}
        items={[t('home.ship1'), t('home.ship2'), t('home.ship3'), t('home.ship4')]}
        note={t('home.shipNote')}
        cta={t('home.shipCta')}
        ctaHref="https://kaspreadsheet.com"
      />

      {/* Coupon */}
      <InfoCard
        id="coupons"
        icon="ri-coupon-3-line"
        title={t('home.couponTitle')}
        desc={t('home.couponP')}
        items={[t('home.coupon1'), t('home.coupon2'), t('home.coupon3'), t('home.coupon4')]}
        note={t('home.couponNote')}
        cta={t('home.couponCta')}
        ctaHref="https://kaspreadsheet.com"
      />

      {/* Warehouse */}
      <InfoCard
        id="warehouse"
        icon="ri-store-3-line"
        title={t('home.wareTitle')}
        desc={t('home.wareP')}
        items={[t('home.ware1'), t('home.ware2'), t('home.ware3'), t('home.ware4'), t('home.ware5')]}
        cta={t('home.wareCta')}
        ctaHref="https://kaspreadsheet.com"
      />

      {/* Legit */}
      <InfoCard
        id="legit"
        icon="ri-shield-check-line"
        title={t('home.legitTitle')}
        desc={t('home.legitP')}
        items={[t('home.legit1'), t('home.legit2'), t('home.legit3'), t('home.legit4'), t('home.legit5')]}
        note={t('home.legitNote')}
        cta={t('home.legitCta')}
        ctaHref="https://kaspreadsheet.com"
      />

      {/* Comparison */}
      <section id="comparison">
        <h2 className="font-heading text-2xl md:text-4xl font-bold text-stone-900 text-center">{t('home.compTitle')}</h2>
        <div className="mt-8 overflow-x-auto rounded-lg border border-stone-200 bg-white">
          <table className="w-full text-left text-sm min-w-[640px]">
            <thead>
              <tr className="bg-emerald-50 text-emerald-900">
                <th className="px-4 py-3 font-semibold">{t('home.compHeader1')}</th>
                <th className="px-4 py-3 font-semibold">{t('home.compHeader2')}</th>
                <th className="px-4 py-3 font-semibold">{t('home.compHeader3')}</th>
                <th className="px-4 py-3 font-semibold">{t('home.compHeader4')}</th>
                <th className="px-4 py-3 font-semibold">{t('home.compHeader5')}</th>
                <th className="px-4 py-3 font-semibold">{t('home.compHeader6')}</th>
              </tr>
            </thead>
            <tbody>
              {compRows.map((row, i) => (
                <tr key={i} className="border-t border-stone-100">
                  {row.map((cell, j) => (
                    <td key={j} className={`px-4 py-3 text-stone-700 ${j === 0 ? 'font-semibold text-stone-900' : ''}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}